<template>
    <div class="config-editor">
        <h1>{{ $tkey('configEditor') }}</h1>
        <v-container fluid>
            <v-row align="start">
                <v-col cols="2">
                    <vuex-select
                        :options="getMongoConfigOptions"
                        :getter="() => selectedConfigId"
                        :setter="configSetter"
                        :label="$tkey('selectConfigLabel')"
                    />
                </v-col>
            </v-row>
        </v-container>
        <template v-if="selectedConfigId">
            <v-container class="config-editor__container" fluid>
                <v-row
                    v-for="(item, itemIndex) in formItems"
                    :key="'item' + itemIndex"
                    align="start"
                    class="config-editor__container--item"
                >
                    <v-col
                        v-for="(field, fieldIndex) in formFields"
                        :key="'field' + fieldIndex"
                        :cols="field.fieldType === 'boolean' ? 2 : 3"
                        :class="field.fieldType === 'boolean' ? 'checkbox-field' : ''"
                    >
                        <component
                            :is="fieldTypeComponentMap[field.fieldType]"
                            :getter="() => item[field.fieldName]"
                            :setter="value => itemSetter(value, item, field)"
                            :label="field.fieldLabel"
                            :validations="showValidation ? fieldValidations(field) : []"
                        />
                    </v-col>
                    <v-col cols="1" class="config-editor__container--delete">
                        <confirm-dialog
                            :question-text="$tkey('deleteDialogQuestion')"
                            :action-text="$tkey('actionText')"
                            @confirm="deleteSelectedItem(itemIndex)"
                        >
                            <template v-slot:actions="{ activator }">
                                <delete-button :events="activator" background />
                            </template>
                        </confirm-dialog>
                    </v-col>
                </v-row>
                <v-row align="start">
                    <v-col cols="1">
                        <create-new-button
                            :btn-text="$tkey('addNewConfigText')"
                            @createNew="createNew"
                        />
                    </v-col>
                    <v-col cols="1" offset="3" class="text-right">
                        <simple-button
                            :light-theme="false"
                            :disabled="!hasChanges"
                            @onClick="cancelChanges"
                        >
                            {{ $tkey('cancel') }}
                        </simple-button>
                    </v-col>
                    <v-col cols="1">
                        <positive-action-button
                            :disabled="!hasChanges || !isFormValid"
                            @onClick="save"
                        >
                            {{ $tkey('save') }}
                        </positive-action-button>
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </div>
</template>

<script>
import { cloneDeep, every } from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import validators from '@/js/validators';

export default {
    localizationKey: 'admin.configEditor',
    data() {
        return {
            selectedConfigId: null,
            fieldTypeComponentMap: {
                string: 'vuex-text-field',
                boolean: 'vuex-checkbox',
            },
            configItemsCopy: null,
            hasChanges: false,
        };
    },
    computed: {
        ...mapGetters('clientConfig', ['getMongoConfigOptions', 'getMongoConfigById']),
        selectedConfig() {
            if (!this.selectedConfigId) {
                return null;
            }
            return this.getMongoConfigById(this.selectedConfigId);
        },
        formFields() {
            if (!this.selectedConfig) {
                return null;
            }
            return this.selectedConfig.fields;
        },
        formItems() {
            if (!this.selectedConfig) {
                return null;
            }
            return this.selectedConfig.items;
        },
        isFormValid() {
            if (!this.formItems || !this.formFields) return false;
            return every(this.formItems, item =>
                every(this.formFields, field => !this.isFieldInvalid(item, field))
            );
        },
        showValidation() {
            return this.hasChanges && !this.isFormValid;
        },
        fieldValidations() {
            return field => {
                if (field.fieldType === 'boolean') return [];
                return [{ validator: validators.required, params: [] }];
            };
        },
    },
    methods: {
        ...mapActions('clientConfig', ['loadMongoConfig', 'saveConfigUpdates']),
        ...mapMutations('clientConfig', ['setConfigItemsById']),
        itemSetter(value, item, field) {
            item[field.fieldName] = value;
            this.hasChanges = true;
        },
        async onClick() {
            this.result = await this.loadMongoConfig();
        },
        createNew() {
            const newItem = this.formFields.reduce((acc, field) => {
                acc[field.fieldName] = field.fieldType === 'boolean' ? false : '';
                return acc;
            }, {});
            this.formItems.push(newItem);
            this.hasChanges = true;
        },
        deleteSelectedItem(index) {
            this.formItems.splice(index, 1);
            this.hasChanges = true;
        },
        configSetter(value) {
            this.selectedConfigId = value;
            const config = this.getMongoConfigById(value);
            if (config) {
                this.configItemsCopy = cloneDeep(this.getMongoConfigById(value).items);
                this.hasChanges = false;
            }
        },
        cancelChanges() {
            if (!this.selectedConfigId || !this.configItemsCopy) return;
            this.setConfigItemsById({
                configItems: cloneDeep(this.configItemsCopy),
                configId: this.selectedConfigId,
            });
            this.hasChanges = false;
        },
        async save() {
            if (!this.isFormValid) return;
            await this.saveConfigUpdates({
                configId: this.selectedConfigId,
                configItems: this.formItems,
            });
            this.configItemsCopy = cloneDeep(this.formItems);
            this.hasChanges = false;
        },
        isFieldInvalid(item, field) {
            if (field.fieldType === 'boolean') return false;
            return validators.required(item[field.fieldName]) !== true;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.config-editor {
    background: $promo-white;
    height: 100%;
    padding: 1rem;

    &__container {
        padding: 1rem;
        background-color: $promo-white;

        &--item {
            padding-bottom: 1rem;
            border-bottom: 0.1rem solid #ccc;
        }

        &--delete {
            align-self: center;
            padding-top: 3.5rem;
        }
    }

    .checkbox-field {
        align-self: flex-end;
    }
}
</style>
